import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [successStories, setSuccessStories] = useState([]);
  const [pwdRequests, setPwdRequests] = useState([]);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') return navigate('/login');
    loadStats();
    loadNotifications();
  }, [user, navigate]);

  useEffect(() => {
    if (tab === 'users' || tab === 'pending') loadUsers();
    if (tab === 'payments') loadPendingPayments();
    if (tab === 'stories') loadStories();
    if (tab === 'pwdreset') loadPwdRequests();
  }, [tab, filterStatus]);

  const loadStats = async () => {
    try { const { data } = await API.get('/api/admin/stats'); setStats(data.stats); } catch {}
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const status = tab === 'pending' ? 'pending' : filterStatus;
      const { data } = await API.get('/api/admin/users', { params: { status, search } });
      setUsers(data.users || []);
    } catch {}
    setLoading(false);
  };

  const loadPendingPayments = async () => {
    try { const { data } = await API.get('/api/admin/pending-payments'); setPendingPayments(data.payments || []); } catch {}
  };

  const loadNotifications = async () => {
    try { const { data } = await API.get('/api/admin/notifications'); setNotifications(data.notifications || []); } catch {}
  };

  const loadStories = async () => {
    try { const { data } = await API.get('/api/admin/success-stories'); setSuccessStories(data.stories || []); } catch {}
  };

  const loadPwdRequests = async () => {
    try { const { data } = await API.get('/api/admin/password-reset-requests'); setPwdRequests(data.requests || []); } catch {}
  };

  const handleResetClientPassword = async () => {
    if (!newPassword || newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setResetLoading(true);
    try {
      await API.put(`/api/admin/reset-client-password/${resetUserId}`, { newPassword });
      toast.success('✅ Password reset! Client notified via in-app notification.');
      setResetUserId(null); setResetUserName(''); setNewPassword('');
      loadPwdRequests(); loadStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    }
    setResetLoading(false);
  };

  const handleApprove = async (userId) => {
    try {
      await API.put(`/api/admin/approve/${userId}`);
      toast.success('Profile approved!');
      loadUsers(); loadStats();
    } catch { toast.error('Failed'); }
  };

  const handleReject = async (userId) => {
    if (!rejectReason) return toast.error('Please enter rejection reason');
    try {
      await API.put(`/api/admin/reject/${userId}`, { reason: rejectReason });
      toast.success('Profile rejected');
      setRejectId(null); setRejectReason('');
      loadUsers(); loadStats();
    } catch { toast.error('Failed'); }
  };

  const handleBlock = async (userId) => {
    if (!window.confirm('Block this user?')) return;
    try {
      await API.put(`/api/admin/block/${userId}`);
      toast.success('User blocked'); loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await API.delete(`/api/admin/user/${userId}`);
      toast.success('User deleted'); loadUsers(); loadStats();
    } catch { toast.error('Failed'); }
  };

  const loadEditUser = async (userId) => {
    try {
      const { data } = await API.get(`/api/admin/user/${userId}`);
      setEditUser(data);
      setTab('edit');
    } catch { toast.error('Failed to load user'); }
  };

  const NAKSHATRAS = ['Ashwini','Bharani','Krittika','Rohini','Mrigashirsha','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hastha','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
  const RASIS = ['Mesha','Rishabha','Mithuna','Kataka','Simha','Kanya','Tula','Vrischika','Dhanus','Makara','Kumbha','Meena'];

  const TABS = [
    { id: 'stats',    label: '📊 Dashboard' },
    { id: 'pending',  label: `⏳ Pending (${stats.pending || 0})` },
    { id: 'users',    label: '👥 All Profiles' },
    { id: 'payments', label: '💰 Payments' },
    { id: 'pwdreset', label: `🔐 Pwd Requests (${stats.pwdRequests || 0})` },
    { id: 'stories',  label: '💑 Stories' },
    { id: 'notifs',   label: '🔔 Notifications' },
  ];

  return (
    <div className="admin-page">
      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <span>🪷</span>
          <div>
            <div style={{color:'var(--gold)',fontWeight:700,fontFamily:'Cormorant Garamond,serif',fontSize:'1rem'}}>Admin Panel</div>
            <div style={{fontSize:'0.7rem',color:'rgba(217,196,169,0.6)'}}>Suvaran Maran</div>
          </div>
        </div>
        <nav className="admin-nav">
          {TABS.map(t => (
            <button key={t.id} className={`admin-nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </nav>
        <div className="admin-info">
          <div style={{fontSize:'0.8rem',color:'rgba(217,196,169,0.6)'}}>Logged in as</div>
          <div style={{color:'var(--gold)',fontSize:'0.9rem'}}>{user?.phone}</div>
        </div>
        <button className="ds-logout" onClick={() => { logout(); navigate('/'); }}>🚪 Logout</button>
      </aside>

      <main className="admin-main">
        {/* STATS TAB */}
        {tab === 'stats' && (
          <div>
            <h1 className="admin-page-title">📊 Dashboard Overview</h1>
            <div className="admin-stats-grid">
              {[
                { icon: '👥', label: 'Total Profiles', value: stats.total || 0, color: 'var(--maroon)' },
                { icon: '✅', label: 'Approved', value: stats.approved || 0, color: '#2d7a4f' },
                { icon: '⏳', label: 'Pending', value: stats.pending || 0, color: '#856404' },
                { icon: '❌', label: 'Rejected', value: stats.rejected || 0, color: '#721c24' },
                { icon: '💰', label: 'Total Revenue', value: `₹${stats.revenue || 0}`, color: '#0c5460' },
                { icon: '📅', label: 'This Month', value: stats.thisMonth || 0, color: 'var(--brown)' },
                { icon: '💑', label: 'Success Stories', value: stats.stories || 0, color: '#4a235a' },
                { icon: '🔐', label: 'Pwd Requests',    value: stats.pwdRequests || 0, color: '#c0392b' },
              ].map(s => (
                <div key={s.label} className="admin-stat-card" style={{borderTopColor: s.color}}>
                  <div className="asc-icon">{s.icon}</div>
                  <div className="asc-value" style={{color: s.color}}>{s.value}</div>
                  <div className="asc-label">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="admin-quick-actions">
              <h2>Quick Actions</h2>
              <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
                <button className="btn btn-primary" onClick={() => setTab('pending')}>⏳ Review Pending Profiles</button>
                <button className="btn btn-outline" onClick={() => setTab('payments')}>💰 Verify Payments</button>
                <button className="btn btn-outline" onClick={() => setTab('users')}>👥 All Profiles</button>
                {(stats.pwdRequests > 0) && (
                  <button className="btn btn-outline" style={{borderColor:'#c0392b',color:'#c0392b'}} onClick={() => setTab('pwdreset')}>
                    🔐 Password Requests ({stats.pwdRequests})
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PENDING / ALL USERS */}
        {(tab === 'pending' || tab === 'users') && (
          <div>
            <div className="admin-section-header">
              <h1 className="admin-page-title">{tab === 'pending' ? '⏳ Pending Approvals' : '👥 All Profiles'}</h1>
              <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
                <input className="form-input" placeholder="Search name / phone..." value={search} onChange={e => setSearch(e.target.value)} style={{width:'220px'}} />
                {tab === 'users' && (
                  <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{width:'140px'}}>
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="inactive">Inactive</option>
                  </select>
                )}
                <button className="btn btn-primary btn-sm" onClick={loadUsers}>🔍 Search</button>
              </div>
            </div>

            {loading ? <div className="loader"><div className="spinner" /></div> :
              users.length === 0 ? <div className="empty-state"><div className="icon">📋</div><h3>No profiles found</h3></div> :
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Profile</th><th>Horoscope</th><th>Payment</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div className="at-profile">
                            {u.photo_path && <img src={`${API_URL}${u.photo_path}`} alt="" className="at-photo" />}
                            <div>
                              <strong>{u.full_name || '—'}</strong>
                              <div style={{fontSize:'0.78rem',color:'var(--brown)'}}>{u.phone}</div>
                              <div style={{fontSize:'0.75rem',color:'var(--text-light)'}}>{u.profile_type} • {u.age} yrs • {u.native_place}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{fontSize:'0.82rem'}}>
                            <div><strong>{u.horoscope_type}</strong></div>
                            <div style={{color:'var(--brown)'}}>⭐ {u.birth_star}</div>
                            <div style={{color:'var(--brown)'}}>♈ {u.rasi}</div>
                          </div>
                        </td>
                        <td>
                          <div style={{fontSize:'0.82rem'}}>
                            <div style={{color: u.payment_status === 'verified' ? 'var(--green)' : u.payment_status === 'rejected' ? 'var(--red)' : 'var(--yellow)'}}>
                              <strong>{u.payment_status || 'No payment'}</strong>
                            </div>
                            {u.transaction_id && <div style={{color:'var(--text-light)',fontSize:'0.72rem'}}>TXN: {u.transaction_id}</div>}
                            {u.amount && <div>₹{u.amount}</div>}
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${u.status}`} style={{textTransform:'capitalize'}}>{u.status}</span>
                          {u.rejection_reason && <div style={{fontSize:'0.72rem',color:'var(--red)',marginTop:'4px'}}>{u.rejection_reason}</div>}
                        </td>
                        <td>
                          <div className="at-actions">
                            {u.status === 'pending' && (
                              <button className="btn btn-sm" style={{background:'#2d7a4f',color:'white'}} onClick={() => handleApprove(u.id)}>✅ Approve</button>
                            )}
                            {u.status !== 'rejected' && u.status !== 'inactive' && (
                              <button className="btn btn-outline btn-sm" style={{borderColor:'var(--red)',color:'var(--red)'}}
                                onClick={() => { setRejectId(u.id); setRejectReason(''); }}>❌ Reject</button>
                            )}
                            <button className="btn btn-outline btn-sm" onClick={() => loadEditUser(u.id)}>✏️ Edit</button>
                            <button className="btn btn-outline btn-sm" style={{borderColor:'#c0392b',color:'#c0392b'}}
                              onClick={() => { setResetUserId(u.id); setResetUserName(u.full_name || u.phone); setNewPassword(''); }}>🔐 Reset</button>
                            <button className="btn btn-outline btn-sm" onClick={() => handleBlock(u.id)} style={{color:'var(--brown)'}}>🚫 Block</button>
                            <button className="btn btn-outline btn-sm" onClick={() => handleDelete(u.id)} style={{color:'var(--red)'}}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }

            {/* Reject Modal */}
            {rejectId && (
              <div className="admin-modal-overlay" onClick={() => setRejectId(null)}>
                <div className="admin-modal" onClick={e => e.stopPropagation()}>
                  <h3>❌ Reject Profile</h3>
                  <p>Please provide a reason for rejection:</p>
                  <textarea className="form-textarea" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
                  <div style={{display:'flex',gap:'10px',marginTop:'16px'}}>
                    <button className="btn btn-primary" onClick={() => handleReject(rejectId)}>Confirm Reject</button>
                    <button className="btn btn-outline" onClick={() => setRejectId(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Reset Password Modal */}
            {resetUserId && (
              <div className="admin-modal-overlay" onClick={() => { setResetUserId(null); setNewPassword(''); }}>
                <div className="admin-modal" onClick={e => e.stopPropagation()}>
                  <h3>🔐 Reset Password</h3>
                  <p>Setting new password for: <strong>{resetUserName}</strong></p>
                  <div className="form-group" style={{marginTop:'16px'}}>
                    <label className="form-label">New Password</label>
                    <input
                      className="form-input"
                      type="text"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  <div style={{background:'#fff8e1',border:'1px solid #f9c74f',borderRadius:'8px',padding:'10px 14px',fontSize:'0.82rem',color:'#856404',marginTop:'12px'}}>
                    ⚠️ After resetting, inform the client of their new password via WhatsApp: <strong>+91 98427 24724</strong>
                  </div>
                  <div style={{display:'flex',gap:'10px',marginTop:'16px'}}>
                    <button className="btn btn-primary" onClick={handleResetClientPassword} disabled={resetLoading}>
                      {resetLoading ? 'Resetting...' : '✅ Confirm Reset'}
                    </button>
                    <button className="btn btn-outline" onClick={() => { setResetUserId(null); setNewPassword(''); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {tab === 'payments' && (
          <div>
            <h1 className="admin-page-title">💰 Payment Verification</h1>
            {pendingPayments.length === 0 ? (
              <div className="empty-state"><div className="icon">💰</div><h3>No pending payments</h3></div>
            ) : pendingPayments.map(p => (
              <div key={p.id} className="payment-verify-card card" style={{padding:'20px',marginBottom:'12px'}}>
                <div className="pvc-info">
                  {p.photo_path && <img src={`${API_URL}${p.photo_path}`} alt="" className="at-photo" />}
                  <div>
                    <strong>{p.full_name || '—'}</strong>
                    <div style={{fontSize:'0.85rem',color:'var(--brown)'}}>📞 {p.phone}</div>
                    <div style={{fontSize:'0.85rem'}}>💳 Transaction ID: <strong>{p.transaction_id}</strong></div>
                    <div style={{fontSize:'0.85rem'}}>₹{p.amount} • {new Date(p.payment_date).toLocaleString('en-IN')}</div>
                  </div>
                </div>
                <div style={{display:'flex',gap:'10px'}}>
                  <button className="btn btn-sm" style={{background:'#2d7a4f',color:'white'}} onClick={() => handleApprove(p.user_id)}>✅ Verify & Approve</button>
                  <button className="btn btn-outline btn-sm" style={{borderColor:'var(--red)',color:'var(--red)'}} onClick={() => { setRejectId(p.user_id); setRejectReason(''); }}>❌ Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 🔐 PASSWORD RESET REQUESTS TAB */}
        {tab === 'pwdreset' && (
          <div>
            <h1 className="admin-page-title">🔐 Password Reset Requests</h1>
            {pwdRequests.length === 0 ? (
              <div className="empty-state">
                <div className="icon">🔐</div>
                <h3>No pending password requests</h3>
                <p>When clients request a password change, they will appear here.</p>
              </div>
            ) : pwdRequests.map(r => (
              <div key={r.id} className="card" style={{padding:'20px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'16px',flexWrap:'wrap'}}>
                {r.photo_path && <img src={`${API_URL}${r.photo_path}`} alt="" className="at-photo" />}
                <div style={{flex:1}}>
                  <strong>{r.full_name || '—'}</strong>
                  <div style={{fontSize:'0.85rem',color:'var(--brown)'}}>📞 {r.phone}</div>
                  <div style={{fontSize:'0.82rem',color:'var(--text-light)'}}>
                    {r.profile_type} • Requested: {new Date(r.requested_at).toLocaleString('en-IN')}
                  </div>
                </div>
                <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
                  <a
                    href={`https://wa.me/91${r.phone}?text=Hi ${r.full_name || ''}, your password for Suvaran Maran Matrimony has been reset. Please login with your new password.`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline btn-sm"
                    style={{color:'#25D366',borderColor:'#25D366'}}
                  >
                    📲 Notify on WhatsApp
                  </a>
                  <button
                    className="btn btn-sm"
                    style={{background:'#c0392b',color:'white'}}
                    onClick={() => { setResetUserId(r.user_id); setResetUserName(r.full_name || r.phone); setNewPassword(''); }}
                  >
                    🔐 Reset Password
                  </button>
                </div>
              </div>
            ))}

            {/* Reset Password Modal */}
            {resetUserId && (
              <div className="admin-modal-overlay" onClick={() => { setResetUserId(null); setNewPassword(''); }}>
                <div className="admin-modal" onClick={e => e.stopPropagation()}>
                  <h3>🔐 Reset Password</h3>
                  <p>Setting new password for: <strong>{resetUserName}</strong></p>
                  <div className="form-group" style={{marginTop:'16px'}}>
                    <label className="form-label">New Password</label>
                    <input
                      className="form-input"
                      type="text"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  <div style={{background:'#fff8e1',border:'1px solid #f9c74f',borderRadius:'8px',padding:'10px 14px',fontSize:'0.82rem',color:'#856404',marginTop:'12px'}}>
                    ⚠️ After resetting, inform the client of their new password via WhatsApp: <strong>+91 98427 24724</strong>
                  </div>
                  <div style={{display:'flex',gap:'10px',marginTop:'16px'}}>
                    <button className="btn btn-primary" onClick={handleResetClientPassword} disabled={resetLoading}>
                      {resetLoading ? 'Resetting...' : '✅ Confirm Reset'}
                    </button>
                    <button className="btn btn-outline" onClick={() => { setResetUserId(null); setNewPassword(''); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SUCCESS STORIES */}
        {tab === 'stories' && (
          <div>
            <h1 className="admin-page-title">💑 Success Stories</h1>
            {successStories.length === 0 ? (
              <div className="empty-state"><div className="icon">💑</div><h3>No success stories yet</h3></div>
            ) : successStories.map(s => (
              <div key={s.id} className="card" style={{padding:'16px',marginBottom:'12px',display:'flex',gap:'16px',alignItems:'center'}}>
                {s.photo_path && <img src={`${API_URL}${s.photo_path}`} alt="" style={{width:60,height:60,borderRadius:'50%',objectFit:'cover'}} />}
                <div style={{flex:1}}>
                  <strong>{s.couple_name}</strong>
                  <div style={{fontSize:'0.82rem',color:'var(--brown)'}}>📍 {s.location}</div>
                  <p style={{fontSize:'0.85rem',margin:'4px 0 0',color:'var(--text-mid)'}}>{s.story}</p>
                </div>
                <button className="btn btn-outline btn-sm" style={{color:'var(--red)'}} onClick={async () => { await API.delete(`/api/admin/success-stories/${s.id}`); loadStories(); }}>🗑️</button>
              </div>
            ))}
          </div>
        )}

        {/* NOTIFICATIONS */}
        {tab === 'notifs' && (
          <div>
            <h1 className="admin-page-title">🔔 Notifications</h1>
            {notifications.length === 0 ? <div className="empty-state"><div className="icon">🔔</div><h3>No notifications</h3></div>
              : notifications.map(n => (
              <div key={n.id} className={`notif-item card ${!n.is_read ? 'unread' : ''}`} style={{padding:'12px 16px',marginBottom:'8px'}}>
                <div className="notif-dot" />
                <div>
                  <strong>{n.title}</strong>
                  <p>{n.message}</p>
                  <span className="notif-time">{new Date(n.created_at).toLocaleString('en-IN')}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EDIT USER */}
        {tab === 'edit' && editUser && (
          <AdminEditProfile
            editUser={editUser}
            onSave={async (fd) => {
              try {
                await API.put(`/api/admin/edit-profile/${editUser.user.id}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
                toast.success('Profile updated!'); setTab('users'); loadUsers();
              } catch { toast.error('Failed to update'); }
            }}
            onCancel={() => setTab('users')}
          />
        )}
      </main>
    </div>
  );
}

function AdminEditProfile({ editUser, onSave, onCancel }) {
  const { user: u, profile: p } = editUser;
  const [form, setForm] = useState({ 
    ...(p || {}), 
    phone: u.phone,
    dob: p?.dob ? new Date(p.dob).toISOString().split('T')[0] : ''
  });
  const [photo, setPhoto] = useState(null);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const NAKSHATRAS = ['Ashwini','Bharani','Krittika','Rohini','Mrigashirsha','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hastha','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
  const RASIS = ['Mesha','Rishabha','Mithuna','Kataka','Simha','Kanya','Tula','Vrischika','Dhanus','Makara','Kumbha','Meena'];
  const HORO_TYPES = ['Sutham','Seva','Rahu Ketu','Seva, Rahu Ketu'];

  const handleSave = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => { if (v !== null && v !== undefined) fd.append(k, v); });
    if (photo) fd.append('photo', photo);
    onSave(fd);
  };

  const API_URL = process.env.REACT_APP_API_URL || '';

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'24px'}}>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>← Back</button>
        <h1 className="admin-page-title" style={{margin:0}}>✏️ Edit Profile: {p?.full_name || u.phone}</h1>
      </div>
      <div className="card" style={{padding:'32px'}}>
        <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>
          {[
            ['profile_type', 'Profile Type', 'select', ['groom','bride']],
            ['full_name', 'Full Name', 'text'],
            ['full_name_tamil', 'Name in Tamil', 'text'],
            ['god_name', 'God Name', 'text'],
            ['dob', 'Date of Birth', 'date'],
            ['tob', 'Time of Birth', 'time'],
            ['blood_group', 'Blood Group', 'select', ['A+','A-','B+','B-','AB+','AB-','O+','O-']],
            ['height', 'Height', 'text'],
            ['birth_star', 'Birth Star', 'select', NAKSHATRAS],
            ['rasi', 'Rasi', 'select', RASIS],
            ['lagnam', 'Lagnam', 'select', RASIS],
            ['horoscope_type', 'Horoscope Type', 'select', HORO_TYPES],
            ['education', 'Education', 'text'],
            ['job_profession', 'Profession', 'text'],
            ['workplace', 'Workplace', 'text'],
            ['income', 'Income', 'text'],
            ['father_name', 'Father Name', 'text'],
            ['mother_name', 'Mother Name', 'text'],
            ['siblings_count', 'Siblings Count', 'number'],
            ['siblings_married', 'Siblings Married', 'number'],
            ['caste_subdivision', 'Caste', 'text'],
            ['has_property', 'Property', 'select', ['Yes','No']],
            ['place_of_birth', 'Place of Birth', 'text'],
            ['native_place', 'Native Place', 'text'],
            ['address', 'Address', 'text'],
            ['alternate_phone', 'Alt Phone', 'text'],
          ].map(([key, label, type, options]) => (
            <div key={key} className="form-group">
              <label className="form-label">{label}</label>
              {type === 'select' ? (
                <select className="form-select" value={form[key] || ''} onChange={e => set(key, e.target.value)}>
                  <option value="">Select...</option>
                  {options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input className="form-input" type={type} value={form[key] || ''} onChange={e => set(key, e.target.value)} />
              )}
            </div>
          ))}
          <div className="form-group" style={{gridColumn:'1/-1'}}>
            <label className="form-label">Change Profile Photo</label>
            <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} className="form-input" />
            {p?.photo_path && !photo && <img src={`${API_URL}${p.photo_path}`} alt="" style={{width:80,marginTop:8,borderRadius:8}} />}
          </div>
        </div>
        <div style={{display:'flex',gap:'12px',marginTop:'24px'}}>
          <button className="btn btn-gold" onClick={handleSave}>💾 Save Changes</button>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}